import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../../utils/SEO';

interface SeoManagerProps {
    page?: 'home';
    customTitle?: string;
    customDescription?: string;
}

const SeoManager = ({
    page = 'home',
    customTitle,
    customDescription
}: SeoManagerProps) => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language;

    // Base URL of the website
    const baseUrl = 'https://too-many-mornings.com';

    // Default metadata for the site
    const defaultMeta = {
        title: 'Too Many Mornings',
        description: t('seo.description', 'Official website of Too Many Mornings. Discover \'This Is Not Our Love\' and more music from the indie rock band breaking boundaries between alternative rock and dark pop.'),
        canonicalUrl: baseUrl,
        ogImage: `${baseUrl}/assets/og-image.jpg`,
        ogType: 'music' as const,
        twitterCard: 'summary_large_image' as const,
        lang: currentLang,
        author: 'Too Many Mornings',
        keywords: [
            'Too Many Mornings',
            'This Is Not Our Love',
            'indie rock',
            'alternative rock',
            'dark pop',
            'Michał Rudol',
            'Jacek Jurewicz'
        ],
    };

    // Page specific metadata
    const pageMeta = {
        home: {
            ...defaultMeta,
            structuredData: {
                '@context': 'https://schema.org',
                '@type': 'MusicGroup',
                'name': 'Too Many Mornings',
                'url': baseUrl,
                'image': `${baseUrl}/assets/cover-optimized.jpg`,
                'description': 'Breaking boundaries between alternative rock and dark pop since 2023.',
                'genre': 'Rock',
                'member': [
                    {
                        '@type': 'Person',
                        'name': 'Michał Rudol',
                        'url': 'https://rudol.me'
                    }
                ],
                'track': [
                    {
                        '@type': 'MusicRecording',
                        'name': 'This Is Not Our Love',
                        'duration': 'PT2M16S'
                    }
                ]
            }
        }
    };

    // Choose the appropriate metadata based on the current page
    const currentMeta = pageMeta[page];

    // Apply custom metadata if provided
    if (customTitle) {
        currentMeta.title = customTitle;
    }

    if (customDescription) {
        currentMeta.description = customDescription;
    }

    // Update language when it changes
    useEffect(() => {
        document.documentElement.lang = currentLang;
    }, [currentLang]);

    // Use the SEO component to update metadata
    return (
        <SEO
            title={currentMeta.title}
            description={currentMeta.description}
            canonicalUrl={currentMeta.canonicalUrl}
            ogImage={currentMeta.ogImage}
            ogType={currentMeta.ogType}
            twitterCard={currentMeta.twitterCard}
            lang={currentMeta.lang}
            author={currentMeta.author}
            keywords={currentMeta.keywords}
            structuredData={currentMeta.structuredData}
        />
    );
};

export default SeoManager; 